import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { money, hm12, todayISO, validMxPhone, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Scissors, ArrowLeft, ArrowRight, Check, Clock, User, Calendar, Loader2, Tag, ChevronLeft } from "lucide-react";

const STEPS = ["Servicio", "Barbero", "Fecha y hora", "Tus datos", "Confirmar"];

const readCachedServices = () => { try { return JSON.parse(localStorage.getItem("nk_services") || "[]"); } catch (e) { return []; } };

export default function Book() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [services, setServices] = useState(readCachedServices);
  const [barbers, setBarbers] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [maxDate, setMaxDate] = useState("");
  const [servicesLoaded, setServicesLoaded] = useState(() => readCachedServices().length > 0);

  const [service, setService] = useState(null);
  const [barber, setBarber] = useState(null);
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [privacy, setPrivacy] = useState(false);
  const [hp, setHp] = useState("");

  useEffect(() => {
    api.get("/public/services").then((r) => { setServices(r.data); try { localStorage.setItem("nk_services", JSON.stringify(r.data)); } catch (e) {} }).catch(() => {}).finally(() => setServicesLoaded(true));
    api.get("/public/barbers").then((r) => setBarbers(r.data));
    api.get("/public/config").then((r) => {
      const days = Number(r.data?.max_booking_days) || 15;
      const d = new Date();
      d.setDate(d.getDate() + days);
      setMaxDate(d.toISOString().slice(0, 10));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (step === 2 && service && barber && date) {
      setLoadingSlots(true);
      setTime("");
      api.get(`/public/availability?service_id=${service.id}&barber_id=${barber.id}&date=${date}`)
        .then((r) => setSlots(r.data.slots)).catch(() => setSlots([]))
        .finally(() => setLoadingSlots(false));
    }
  }, [step, service, barber, date]);

  const availBarbers = barbers.filter((b) => !service || (b.services_ids || []).includes(service.id) || (b.services_ids || []).length === 0);

  const applyCoupon = async () => {
    if (!coupon) return;
    try {
      const { data } = await api.get(`/public/coupon/${coupon}`);
      setCouponInfo(data);
      toast.success("Cupón aplicado");
    } catch (e) {
      setCouponInfo(null);
      toast.error(apiError(e));
    }
  };

  const discount = couponInfo ? (couponInfo.discount_type === "percent" ? (service.price * couponInfo.discount_value) / 100 : Math.min(couponInfo.discount_value, service?.price || 0)) : 0;

  const submit = async () => {
    if (!privacy) { toast.error("Debes aceptar el aviso de privacidad"); return; }
    if (!validMxPhone(form.phone)) { toast.error("Ingresa un teléfono válido de 10 dígitos"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/book", {
        service_id: service.id, barber_id: barber.id, date, time,
        name: form.name, phone: form.phone, email: form.email, notes: form.notes,
        coupon_code: couponInfo ? couponInfo.code : null, accept_privacy: true, website: hp,
      });
      navigate(`/booking-success/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const canNext = () => {
    if (step === 0) return !!service;
    if (step === 1) return !!barber;
    if (step === 2) return !!time;
    if (step === 3) return form.name && validMxPhone(form.phone);
    return true;
  };

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-[#f5f5f5]">
      <header className="border-b border-[#2a2a2a] h-16 flex items-center px-5">
        <Link to="/" className="flex items-center gap-2 text-[#a3a3a3] hover:text-[#f5f5f5]" data-testid="book-back-home">
          <ArrowLeft className="w-4 h-4" /> <Scissors className="w-5 h-5 text-[#d4af37]" /> <span className="font-bold">NAKAMA</span>
        </Link>
      </header>

      <div className="max-w-3xl mx-auto px-5 py-8">
        {/* honeypot anti-bot: invisible para humanos */}
        <input type="text" name="website" value={hp} onChange={(e) => setHp(e.target.value)}
          tabIndex={-1} autoComplete="off" aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
        {/* Stepper */}
        <div className="flex items-center justify-between mb-10">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1 flex items-center">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-colors ${i < step ? "bg-[#16a34a] text-white" : i === step ? "bg-[#d4af37] text-black" : "bg-[#242424] text-[#737373]"}`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i < step ? "bg-[#16a34a]" : "bg-[#2a2a2a]"}`} />}
            </div>
          ))}
        </div>
        <h2 className="text-2xl font-extrabold mb-1">{STEPS[step]}</h2>
        <p className="text-[#737373] text-sm mb-6">Paso {step + 1} de {STEPS.length}</p>

        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
          {step === 0 && servicesLoaded && services.length === 0 && (
            <p className="text-[#737373] text-center py-16" data-testid="book-no-services">No hay servicios disponibles por ahora. Intenta más tarde.</p>
          )}
          {step === 0 && services.length === 0 && !servicesLoaded && (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="book-services-loading">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="nk-card p-5 animate-pulse">
                  <div className="h-4 w-2/3 bg-[#242424] rounded mb-3" />
                  <div className="h-3 w-1/3 bg-[#1c1c1c] rounded mb-5" />
                  <div className="h-5 w-1/4 bg-[#242424] rounded" />
                </div>
              ))}
            </div>
          )}
          {step === 0 && services.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-4">
              {services.map((s) => (
                <button key={s.id} onClick={() => setService(s)} data-testid={`book-service-${s.id}`}
                  className={`nk-card p-5 text-left transition-all ${service?.id === s.id ? "border-[#d4af37] ring-1 ring-[#d4af37]" : "hover:border-[#d4af37]/40"}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold">{s.name}</h3>
                      <p className="text-xs text-[#737373] mt-1 flex items-center gap-1"><Clock className="w-3 h-3" />{s.duration_min} min</p>
                    </div>
                    <span className="text-[#d4af37] font-bold">{money(s.price)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="grid sm:grid-cols-2 gap-4">
              {availBarbers.map((b) => (
                <button key={b.id} onClick={() => setBarber(b)} data-testid={`book-barber-${b.id}`}
                  className={`nk-card p-5 flex items-center gap-4 text-left transition-all ${barber?.id === b.id ? "border-[#d4af37] ring-1 ring-[#d4af37]" : "hover:border-[#d4af37]/40"}`}>
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-[#242424] shrink-0">
                    {b.photo_url ? <img src={b.photo_url} alt={b.name} className="w-full h-full object-cover" /> : <User className="w-6 h-6 m-4 text-[#737373]" />}
                  </div>
                  <div>
                    <h3 className="font-bold">{b.name}</h3>
                    <p className="text-xs text-[#737373]">{b.bio}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div>
              <label className="text-sm text-[#a3a3a3] mb-1.5 block">Selecciona el día</label>
              <input type="date" min={todayISO()} max={maxDate || undefined} value={date} onChange={(e) => setDate(e.target.value)} className="nk-input nk-date max-w-xs mb-6" data-testid="book-date" />
              {loadingSlots ? (
                <div className="flex items-center gap-2 text-[#737373]"><Loader2 className="w-4 h-4 animate-spin" /> Buscando horarios...</div>
              ) : slots.length === 0 ? (
                <p className="text-[#737373]">No hay horarios disponibles este día. Prueba con otra fecha.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {slots.map((s) => (
                    <button key={s} onClick={() => setTime(s)} data-testid={`book-slot-${s}`}
                      className={`py-2.5 rounded-[10px] text-sm font-medium transition-colors ${time === s ? "bg-[#d4af37] text-black" : "bg-[#242424] text-[#f5f5f5] hover:bg-[#2f2f2f]"}`}>
                      {hm12(s)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 max-w-lg">
              <div><label className="text-sm text-[#a3a3a3] mb-1.5 block">Nombre completo *</label>
                <input className="nk-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="book-name" placeholder="Juan Pérez" /></div>
              <div><label className="text-sm text-[#a3a3a3] mb-1.5 block">Teléfono (10 dígitos) *</label>
                <input className="nk-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="book-phone" placeholder="9991234567" /></div>
              <div><label className="text-sm text-[#a3a3a3] mb-1.5 block">Correo (para tu confirmación)</label>
                <input className="nk-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="book-email" placeholder="tucorreo@email.com" />
                <p className="text-xs text-[#c9a24b] mt-1.5 leading-relaxed" data-testid="book-email-hint">Agregando tu correo podrás recibir puntos y beneficios exclusivos en cada servicio, y reagendar o cancelar tu cita.</p></div>
              <div><label className="text-sm text-[#a3a3a3] mb-1.5 block">Notas (opcional)</label>
                <textarea className="nk-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="book-notes" /></div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <div className="nk-card p-6 space-y-3">
                <Row label="Servicio" value={service?.name} />
                <Row label="Barbero" value={barber?.name} />
                <Row label="Fecha" value={fmtDate(date)} />
                <Row label="Hora" value={hm12(time)} />
                <Row label="Cliente" value={form.name} />
                <div className="border-t border-[#2a2a2a] pt-3">
                  <Row label="Precio" value={money(service?.price)} />
                  {discount > 0 && <Row label="Descuento" value={`- ${money(discount)}`} accent />}
                  <div className="flex justify-between mt-2"><span className="font-bold">Total</span><span className="font-bold text-[#d4af37] text-lg">{money((service?.price || 0) - discount)}</span></div>
                </div>
              </div>
              <div>
                <label className="text-sm text-[#a3a3a3] mb-1.5 block">¿Tienes un cupón?</label>
                <div className="flex gap-2 max-w-sm">
                  <input className="nk-input" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="NAKAMA10" data-testid="book-coupon" />
                  <button onClick={applyCoupon} className="nk-btn-ghost flex items-center gap-1 shrink-0" data-testid="book-apply-coupon"><Tag className="w-4 h-4" />Aplicar</button>
                </div>
              </div>
              <label className="flex items-start gap-2 text-sm text-[#a3a3a3] cursor-pointer">
                <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} data-testid="book-privacy" className="mt-1 accent-[#d4af37]" />
                <span>Acepto el aviso de privacidad y el tratamiento de mis datos para gestionar mi cita.</span>
              </label>
            </div>
          )}
        </motion.div>

        <div className="flex justify-between mt-10">
          <button onClick={() => (step === 0 ? navigate("/") : setStep(step - 1))}
            className="nk-btn-ghost flex items-center gap-2" data-testid="book-prev">
            <ChevronLeft className="w-4 h-4" /> Atrás
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()}
              className="nk-btn flex items-center gap-2 disabled:opacity-40" data-testid="book-next">
              Continuar <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting || !privacy} className="nk-btn flex items-center gap-2" data-testid="book-confirm">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar reserva
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-[#737373]">{label}</span>
      <span className={accent ? "text-[#16a34a]" : "text-[#f5f5f5] font-medium"}>{value}</span>
    </div>
  );
}
